import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, ArrowLeftRight, ShieldAlert } from "lucide-react";
import { startActAs } from "@/components/ActAsBanner";
import { toast } from "sonner";
import { API, fmtScore, bandMeta } from "./shared";
import { PartialTag } from "./PartialTag";
import { GapTrendBadge, useLargestGapTrend } from "./GapTrendBadge";
import {
  SCHOOL_COLOR, PORTFOLIO_COLOR, FLOW_LABEL, flowState, compareState, periodsForSchool,
  openPortfolioAssessment, assessmentActionLabel,
} from "./portfolioFlow";

const BandTag = ({ score }) => {
  if (score === null || score === undefined) return null;
  const m = bandMeta(score);
  return <span className="inline-block rounded-md px-2 py-0.5 text-xs font-semibold" style={{ background: m.bg, color: m.fg }}>{m.label}</span>;
};

const Card = ({ color, title, badge, testid, children }) => (
  <div data-testid={testid} className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white overflow-hidden">
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-white" style={{ background: color }}>
      <span className="font-semibold inline-flex items-center gap-2">{title}</span>
      {badge && <span className="rounded-full border border-white/40 bg-white/15 px-2.5 py-0.5 text-xs font-semibold">{badge}</span>}
    </div>
    <div className="p-4 space-y-2">{children}</div>
  </div>
);

export const SchoolHub = ({ pid, tenantId }) => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState(null);
  const [sel, setSel] = useState("");
  const [cmp, setCmp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const trend = useLargestGapTrend(pid, { tenantId, periodId: sel || undefined });
  useEffect(() => {
    fetch(`${API}/api/portfolio/me`, { credentials: "include" }).then((r) => r.json()).then((m) => setIsAdmin(!!m.is_admin)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!pid) return;
    fetch(`${API}/api/portfolio/${pid}/periods`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { periods: [] }))
      .then((d) => { const rows = periodsForSchool(d.periods, tenantId); setPeriods(rows); if (rows[0]) setSel(rows[0].period.id); })
      .catch(() => setPeriods([]));
  }, [pid, tenantId]);

  const cur = useMemo(() => (periods || []).find((x) => x.period.id === sel), [periods, sel]);
  const included = !!cur?.period.enable_portfolio_assessments;

  useEffect(() => {
    setCmp(null);
    if (!cur || !included) return;
    fetch(`${API}/api/portfolio/${pid}/periods/${cur.period.id}/comparison`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setCmp((d?.schools || []).find((s) => s.tenant_id === tenantId) || null))
      .catch(() => {});
  }, [cur, included, pid, tenantId]);

  const actAs = isAdmin && (
    <button onClick={() => startActAs(tenantId).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't act as this school"))}
      data-testid="hub-act-as" className="mr-auto inline-flex items-center gap-1.5 rounded-md bg-[#f59e0b] px-3 py-2 text-sm font-semibold text-[#111827]">
      <ShieldAlert className="h-4 w-4" /> Act as this school
    </button>
  );
  if (periods === null) return <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;
  if (!periods.length) return actAs ? <div className="flex">{actAs}</div> : null;

  const school = flowState(cur?.req.status);
  const mine = flowState(cur?.req.portfolio_status);
  const cs = compareState({ school, portfolio: mine, included });
  const sScore = school === "final" ? cmp?.school?.overall : null;
  const pScore = mine === "final" ? cmp?.portfolio?.overall : null;

  const startMine = async () => {
    setBusy(true);
    try { await openPortfolioAssessment(pid, cur.period.id, tenantId); } catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <section data-testid="school-hub" className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {actAs}
        <GapTrendBadge trend={trend[tenantId]} testid="hub-gap-trend" />
        <label htmlFor="hub-period" className="text-sm text-slate-500">Diagnostic period</label>
        <select id="hub-period" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="hub-period-select"
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
          {periods.map((x) => <option key={x.period.id} value={x.period.id}>{x.period.name}</option>)}
        </select>
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <Card color={SCHOOL_COLOR} testid="hub-school-card" title={<><Lock className="h-4 w-4" /> School's Diagnostic</>} badge={school === "final" ? "Submitted by the school · View only" : "View only"}>
          <span className="text-xs font-semibold text-slate-600" data-testid="hub-school-status">{FLOW_LABEL[school]}</span>
          <div className="text-3xl font-bold text-slate-900">{sScore != null ? fmtScore(sScore) : "—"} <span className="text-sm font-normal text-slate-500">/ 100</span></div>
          <BandTag score={sScore} />
          <PartialTag answered={cmp?.school?.answered} total={cmp?.school?.total} testid="hub-school-partial" />
          <p className="text-sm text-slate-500">Only the school can change its answers.</p>
          <button onClick={() => navigate(`/portfolio/schools/${tenantId}/diagnostic?pid=${pid}`)} data-testid="hub-view-school"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold hover:bg-slate-50">School's diagnostic</button>
        </Card>
        <Card color={PORTFOLIO_COLOR} testid="hub-portfolio-card" title="Your Portfolio Assessment" badge="Private">
          {included ? (
            <>
              <span className="text-xs font-semibold text-slate-600" data-testid="hub-portfolio-status">{FLOW_LABEL[mine]}</span>
              <div className="text-3xl font-bold text-slate-900">{pScore != null ? fmtScore(pScore) : "—"} <span className="text-sm font-normal text-slate-500">/ 100</span></div>
              <BandTag score={pScore} />
              <PartialTag answered={cmp?.portfolio?.answered} total={cmp?.portfolio?.total} testid="hub-portfolio-partial" />
              <p className="text-sm text-slate-500">Private to your Portfolio. The school never sees it.</p>
              <button onClick={startMine} disabled={busy} data-testid="hub-portfolio-action"
                className="rounded-md px-3 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PORTFOLIO_COLOR }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : assessmentActionLabel(mine)}
              </button>
            </>
          ) : (
            <p className="text-sm text-slate-600" data-testid="hub-portfolio-not-included">Not included in this period</p>
          )}
        </Card>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5 text-center">
        <button disabled={!cs.enabled} onClick={() => navigate(`/portfolio/compare/${cur.period.id}/${tenantId}?pid=${pid}`)} data-testid="hub-compare-btn"
          className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white disabled:bg-slate-300 disabled:text-slate-600"
          style={cs.enabled ? { background: SCHOOL_COLOR } : undefined}>
          <ArrowLeftRight className="h-4 w-4" /> Compare School vs Portfolio
          {cs.tag && <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-xs">{cs.tag}</span>}
        </button>
        {cs.reason && <p className="mt-2 text-sm text-slate-600" data-testid="hub-compare-reason">{cs.reason}</p>}
        <div className="mt-2 flex justify-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SCHOOL_COLOR }} />School</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: PORTFOLIO_COLOR }} />Portfolio</span>
        </div>
      </div>
    </section>
  );
};
